import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { statusStyle, formatPhone } from "@/lib/clients";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Pencil, Trash2, Eye, Mail, UserPlus } from "lucide-react";

function RowActions({ client, isAdmin, navigate, onEdit, onDelete, onAssign }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`client-actions-${client.id}`} className="p-1.5 rounded-sm hover:bg-gray-100 text-gray-500">
          <MoreVertical className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-sm">
        <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}`)} data-testid={`view-${client.id}`}>
          <Eye className="w-4 h-4 mr-2" /> View details
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onEdit(client)} data-testid={`edit-${client.id}`}>
          <Pencil className="w-4 h-4 mr-2" /> Edit
        </DropdownMenuItem>
        {isAdmin && (
          <DropdownMenuItem onClick={() => onAssign(client)} data-testid={`assign-${client.id}`}>
            <UserPlus className="w-4 h-4 mr-2" /> Assign members
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => onDelete(client)} className="text-red-600 focus:text-red-600" data-testid={`delete-${client.id}`}>
          <Trash2 className="w-4 h-4 mr-2" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ClientRow({ client: c, isAdmin, onEdit, onDelete, onAssign }) {
  const navigate = useNavigate();
  const names = c.assigned_user_names || [];
  const tags = c.tags || [];
  return (
    <tr className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors" data-testid={`client-row-${c.id}`}>
      <td className="py-3 px-4">
        <button onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-id-${c.id}`} title="View client details"
          className="inline-block font-mono text-xs font-bold tracking-wider text-[#002FA7] bg-[#002FA7]/10 px-2 py-1 rounded-sm hover:bg-[#002FA7]/20 transition-colors">
          {c.client_id || "—"}
        </button>
      </td>
      <td className="py-3 px-4">
        <button onClick={() => navigate(`/clients/${c.id}`)} className="flex items-center gap-3 text-left">
          <div className="w-8 h-8 rounded-full bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center font-bold text-xs shrink-0">
            {c.name?.[0]?.toUpperCase()}
          </div>
          <span className="text-sm font-semibold text-gray-900 hover:text-[#002FA7]">{c.name}</span>
        </button>
      </td>
      <td className="py-3 px-4 text-sm text-gray-600">{c.company || "—"}</td>
      <td className="py-3 px-4 text-sm text-gray-600">
        <div className="truncate max-w-[180px]">{c.email || "—"}</div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{formatPhone(c.phone)}</span>
          {(c.email_count || 0) > 0 && (
            <span data-testid={`comm-stat-${c.id}`} title={c.last_email_at ? `Last email ${new Date(c.last_email_at).toLocaleDateString()}` : ""}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-sm">
              <Mail className="w-3 h-3" /> {c.email_count}
            </span>
          )}
        </div>
      </td>
      <td className="py-3 px-4">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm border capitalize ${statusStyle(c.status)}`}>{c.status}</span>
      </td>
      <td className="py-3 px-4">
        <div className="flex flex-wrap gap-1 max-w-[160px]" data-testid={`assigned-chips-${c.id}`}>
          {names.slice(0, 2).map((n) => (
            <span key={n} className="text-xs font-medium bg-blue-50 text-[#002FA7] border border-blue-100 px-1.5 py-0.5 rounded-sm">{n}</span>
          ))}
          {names.length > 2 && <span className="text-xs text-gray-400">+{names.length - 2}</span>}
          {names.length === 0 && <span className="text-xs text-gray-300">—</span>}
        </div>
      </td>
      <td className="py-3 px-4">
        <div className="flex flex-wrap gap-1 max-w-[160px]">
          {tags.slice(0, 3).map((t) => (
            <span key={t} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-sm">{t}</span>
          ))}
          {tags.length > 3 && <span className="text-xs text-gray-400">+{tags.length - 3}</span>}
        </div>
      </td>
      <td className="py-3 px-4 text-right">
        <RowActions client={c} isAdmin={isAdmin} navigate={navigate} onEdit={onEdit} onDelete={onDelete} onAssign={onAssign} />
      </td>
    </tr>
  );
}
